/* eslint-disable prefer-promise-reject-errors */

import { AuthProvider, UserIdentity } from 'react-admin'
import { client as gqlClient } from '@/stackGqlClient'
import userInfoQuery from '@/providers/Auth/queries/userInfoQuery'
import CONSTANTS from '@/constants'
import Storage from '@/lib/Storage'
import canAccess from '@/providers/Auth/canAccess'
import Analytics from '@/lib/segment/analytics'
import { getAccessTokenPayload } from '@/lib/jwt/decode'

const userKey = 'user'
const roleKey = 'activeRole'
const refreshTokenKey = CONSTANTS.REFRESH_TOKEN_STORAGE_KEY
const accessTokenKey = CONSTANTS.ACCESS_TOKEN_STORAGE_KEY
const authURL = `${import.meta.env.VITE_STACK_API_BASE_URL}/org_users/auth`
const rolePickerRoute = '/roles'
export type RoleType = 'PUBLISHER' | 'VENDOR' | 'AFFILIATE'

// TODO: This probably should live with the role picker code once implemented
export interface UserRole {
  id: number
  type: RoleType
  name: string
  // For publishers, the hostname. For others, company name.
  subname: string
  organizationId: number
}

export interface ScopedUserIdentity extends UserIdentity {
  role: UserRole
}

export const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    const recaptchaToken = Storage().getRecaptchaToken()
    const tokenRequest = new Request(authURL, {
      method: 'POST',
      body: JSON.stringify({
        email: username,
        password,
        recaptcha_token: recaptchaToken
      }),
      headers: new Headers({ 'Content-Type': 'application/json' })
    })
    try {
      const response = await fetch(tokenRequest)
      persistTokenResponse(await response.json())
    } catch (_error) {
      throw new Error(
        'Unable to login. Please check your credentials or try again later.'
      )
    }
    try {
      return await loadUserInfo()
    } catch {
      throw new Error(
        'An error occurred retrieving your account information. Please try again later.'
      )
    }
  },
  logout: async () => {
    localStorage.clear()
    await gqlClient.clearStore()

    document.cookie.split(';').forEach((cookie) => {
      const [name] = cookie.trim().split('=')
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    })
  },
  // Log the user out on a 401 or 403 error
  checkError: (error: { status: number }) => {
    const status = error.status
    return status === 401 || status === 403
      ? Promise.reject()
      : Promise.resolve()
  },
  checkAuth: () =>
    localStorage.getItem(accessTokenKey) ? Promise.resolve() : Promise.reject(),
  // We're not currently using permissions beyond those enforced by API.
  getPermissions: () => {
    return Promise.resolve(undefined)
  },
  getIdentity(): Promise<ScopedUserIdentity> {
    return Promise.resolve(loadIdentity())
  },
  canAccess
}

function persistTokenResponse(response: {
  access_token: string
  refresh_token: string
}) {
  if (!response.access_token || !response.refresh_token) {
    throw new Error('Invalid token response')
  }
  localStorage.setItem(accessTokenKey, response.access_token)
  localStorage.setItem(refreshTokenKey, response.refresh_token)
}

async function loadUserInfo() {
  // TODO: If the user was logged in and their session expired, we should take them back to where they were

  const {
    id,
    fullName,
    admin,
    publishers,
    vendors,
    affiliates,
    email,
    mustUpdatePassword
  } = await userInfoQuery(gqlClient)

  const roles = publishers.concat(vendors, affiliates)
  if (roles.length < 1) {
    throw new Error('No partner roles found')
  }
  const multirole = admin || roles.length > 1
  localStorage.setItem(
    userKey,
    JSON.stringify({
      id,
      fullName,
      email,
      multirole
    })
  )
  Analytics().identify({ id: id.toString(), name: fullName, admin })

  if (mustUpdatePassword) {
    return { redirectTo: '/update-password' }
  }

  if (isAdmin()) {
    return { redirectTo: rolePickerRoute }
  }

  localStorage.setItem(roleKey, JSON.stringify(roles[0]))
}

// This function powers the AuthProvider's getIdentity hook but is named differently to attempt to head off confusion.
// Unlike UseGetIdentity, this is a synchronous function. If you are in a component, use UseGetIdentity!
function loadIdentity(): ScopedUserIdentity {
  const persistedUser = localStorage.getItem(userKey)
  const persistedRole = localStorage.getItem(roleKey)
  const identity = persistedUser ? JSON.parse(persistedUser) : null
  if (identity !== null && persistedRole !== null) {
    identity.role = JSON.parse(persistedRole)
  }
  return identity
}

export function currentRole(): UserRole | undefined {
  return loadIdentity()?.role
}

export function isAdmin(): boolean {
  const payload = getAccessTokenPayload()
  return payload?.data?.attributes?.admin === true
}

export function isMultirole(): boolean {
  const data = localStorage.getItem(userKey)
  if (!data) return false
  const user = JSON.parse(data)
  return user.multirole === true
}

type UserProps = {
  id: number
  email: string
  fullName: string
  multirole: boolean
}

export function currentUser(): UserProps | null {
  const data = Storage().getObject(userKey)
  if (!data) return null

  return data
}

export const matchesRolesTypes = (types: RoleType[]) => {
  const data = localStorage.getItem(roleKey)

  if (!data) return false

  const userRole = JSON.parse(data)
  return types.includes(userRole.type)
}

export function isPublisher(): boolean {
  const data = localStorage.getItem(roleKey)

  if (!data) return false

  const role = JSON.parse(data)
  return role.type === 'PUBLISHER'
}

export function isAffiliate(): boolean {
  const data = localStorage.getItem(roleKey)

  if (!data) return false

  const role = JSON.parse(data)
  return role.type === 'AFFILIATE'
}

export function isVendor(): boolean {
  const data = localStorage.getItem(roleKey)

  if (!data) return false

  const role = JSON.parse(data)
  return role.type === 'VENDOR'
}

export function isMultirolePartner(): boolean {
  return !isAdmin() && isMultirole()
}

export default authProvider
